/** @jsxImportSource @emotion/react */
import { Box, IconButton, useTheme } from '@mui/material'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  carouselContainer,
  carousel,
  carouselControls,
  buttonStyle,
  leftButtonStyle,
  rightButtonStyle,
  carouselCentered,
  carouselContent,
  childWrapper,
  dotsContainer,
  dotStyle,
  carouselCenter,
} from './Carousel.styles'

interface CarouselProps {
  children: React.ReactNode[]
  repeating?: boolean
  childWidth?: number
  childGap?: number
  childWidthIsPercentage?: boolean
  onFocusChange?: (index: number) => void
}

// Child width, if a percentage, is calculated based on the carousel width
export const Carousel = ({
  children,
  repeating = false,
  childWidth = 50,
  childGap = 0,
  childWidthIsPercentage = true,
  onFocusChange,
}: CarouselProps) => {
  const theme = useTheme()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [repeatIndex, setRepeatIndex] = useState(0) // For repeating carousel, tracks how many times the carousel has looped
  const [carouselWidth, setCarouselWidth] = useState(0)
  const carouselRef = useRef<HTMLDivElement>(null)
  const carouselChildren = useMemo(() => {
    if (!repeating) return children.map((child, i) => ({ index: i, repeatIndex, child }))
    const repeatedChildren = []
    for (let ri = repeatIndex - 1; ri <= repeatIndex + 1; ri++) {
      repeatedChildren.push(...children.map((child, i) => ({ index: i, repeatIndex: ri, child })))
    }
    return repeatedChildren
  }, [children, repeatIndex, repeating])

  // Update carouselWidth when the carousel's sizes changes
  useEffect(() => {
    const handleResize = () => {
      if (carouselRef.current) {
        setCarouselWidth(carouselRef.current.offsetWidth)
      }
    }

    // Use ResizeObserver to track changes
    const resizeObserver = new ResizeObserver(handleResize)
    if (carouselRef.current) {
      resizeObserver.observe(carouselRef.current)
    }

    // Cleanup observer on unmount
    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  const calculatedChildWidth = useMemo(() => {
    if (childWidthIsPercentage && childWidth) {
      return (carouselWidth * childWidth) / 100
    }
    return childWidth || 400 // Default width if not provided
  }, [childWidth, carouselWidth, childWidthIsPercentage])

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => {
      if (prevIndex === 0) {
        setRepeatIndex((prevRepeatIndex) => prevRepeatIndex - 1)
        return children.length - 1
      }
      return prevIndex - 1
    })
  }

  const handleNext = () => {
    setCurrentIndex((prevIndex) => {
      if (prevIndex === children.length - 1) {
        setRepeatIndex((prevRepeatIndex) => prevRepeatIndex + 1)
        return 0
      }
      return prevIndex + 1
    })
  }

  const handleDotClick = (index: number) => {
    setCurrentIndex(index)
  }

  // scroll event
  const handleScroll = (event: WheelEvent) => {
    // Check if the event's default behavior has been prevented
    if (event.defaultPrevented) {
      return
    }

    // Traverse up the DOM tree to check for scrollable parents
    let target: HTMLElement | null = event.target as HTMLElement
    while (target && !target.classList.contains('carousel-scrollable-parent')) {
      if (target.scrollHeight > target.clientHeight || target.scrollWidth > target.clientWidth) {
        return // Stop handling if a scrollable parent is found
      }
      target = target.parentElement // Move up the DOM tree
    }

    // Handle carousel scrolling
    if (event.deltaY > 0) {
      handleNext()
    } else {
      handlePrev()
    }
  }
  // add event listener to the carouselRef
  useEffect(() => {
    const carouselElement = carouselRef.current
    if (carouselElement) {
      carouselElement.addEventListener('wheel', handleScroll, { passive: true })
    }
    return () => {
      if (carouselElement) {
        carouselElement.removeEventListener('wheel', handleScroll)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Call the onFocusChange callback when currentIndex changes
  useEffect(() => {
    if (onFocusChange) {
      onFocusChange(currentIndex)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex])

  return (
    <Box css={carouselContainer(theme)}>
      <Box css={carousel}>
        <Box css={carouselControls(theme)}>
          <IconButton
            css={[buttonStyle(theme), leftButtonStyle(theme)]}
            onClick={handlePrev}
            disabled={currentIndex === 0 && !repeating}
          >
            <ArrowBackIosNewIcon />
          </IconButton>
          <IconButton
            css={[buttonStyle(theme), rightButtonStyle(theme)]}
            onClick={handleNext}
            disabled={currentIndex === children.length - 1 && !repeating}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        </Box>
        <Box css={carouselCentered} ref={carouselRef}>
          <Box css={carouselContent} className="carousel-scrollable-parent">
            <Box css={carouselCenter(calculatedChildWidth)}>
              {carouselChildren.map((child) => (
                <Box
                  key={`${child.index}-${child.repeatIndex}`}
                  css={childWrapper(
                    child.index,
                    child.repeatIndex,
                    currentIndex,
                    repeatIndex,
                    children.length,
                    calculatedChildWidth,
                    childGap,
                  )}
                >
                  {child.child}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box css={dotsContainer(theme)}>
        {children.map((_, index) => (
          <Box
            key={index}
            css={dotStyle(theme, index === currentIndex)}
            onClick={() => handleDotClick(index)}
          />
        ))}
      </Box>
    </Box>
  )
}
